import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import Label from '@/common/components/Label.tsx'
import Textarea from '@/common/components/Textarea.tsx'
import { usePutProviderAffiliation } from '@/queries/providers/affiliations/hooks.ts'
import {
  ProviderAffiliation,
  ProviderAffiliationLogoInput,
} from '@/queries/providers/affiliations/schema.ts'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog.tsx'
import { Switch } from '@/shad/ui/switch'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import AffiliationLogoField from './AffiliationLogoField.tsx'
import { Form } from '@/app/common/components/Form.tsx'
import FormErrorMessage from '@/app/common/components/FormErrorMessage.tsx'

const editAffiliationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Name must be 255 characters or less'),
  description: z
    .string()
    .trim()
    .max(255, 'Description must be 255 characters or less')
    .optional(),
  logo_image: z.custom<ProviderAffiliationLogoInput>().optional(),
  status: z.boolean(),
})

type EditAffiliationFormValues = {
  name: string
  description?: string
  logo_image?: ProviderAffiliationLogoInput
  status: boolean
}

type Props = {
  affiliation: ProviderAffiliation
  onOpenChange: (open: boolean) => void
  onEditAffiliationSuccess: () => void
}

function getDefaultValues(
  affiliation: ProviderAffiliation
): EditAffiliationFormValues {
  return {
    name: affiliation.name,
    description: affiliation.description ?? '',
    logo_image: affiliation.logo,
    status: affiliation.status === 'Active',
  }
}

export default function EditAffiliationPopup({
  affiliation,
  onOpenChange,
  onEditAffiliationSuccess,
}: Props) {
  const { toast } = useToast()

  const form = useForm<EditAffiliationFormValues>({
    resolver: zodResolver(editAffiliationSchema),
    defaultValues: getDefaultValues(affiliation),
  })

  const updateAffiliationMutation = usePutProviderAffiliation({
    onSuccess: () => {
      toast({
        title: 'Affiliation Updated',
        description: 'The affiliation has been updated successfully.',
        variant: 'default',
      })
      onEditAffiliationSuccess()
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleClose = () => {
    form.reset(getDefaultValues(affiliation))
    onOpenChange(false)
  }

  const onSubmit = (data: EditAffiliationFormValues) => {
    updateAffiliationMutation.mutate({
      id: affiliation.id,
      data: {
        name: data.name.trim(),
        description: data.description?.trim() ? data.description.trim() : null,
        logo_image:
          data.logo_image instanceof File || data.logo_image === null
            ? data.logo_image
            : undefined,
        status: data.status ? 1 : 0,
      },
    })
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open && !updateAffiliationMutation.isPending) {
          handleClose()
        }
      }}
    >
      <DialogContent
        className="sm:max-w-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Edit Affiliation</DialogTitle>
        </DialogHeader>

        <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="edit-affiliation-name" isRequired>
              Name
            </Label>
            <Input
              id="edit-affiliation-name"
              {...form.register('name')}
              disabled={updateAffiliationMutation.isPending}
              aria-invalid={form.formState.errors.name ? 'true' : 'false'}
            />
            <FormErrorMessage message={form.formState.errors.name?.message} />
          </div>
          <div>
            <Label htmlFor="edit-affiliation-description">
              Description (Optional)
            </Label>
            <Textarea
              id="edit-affiliation-description"
              {...form.register('description')}
              name="description"
              rows={4}
              placeholder="Enter description"
              disabled={updateAffiliationMutation.isPending}
            />
            <FormErrorMessage
              message={form.formState.errors.description?.message}
            />
          </div>
          <Controller
            control={form.control}
            name="logo_image"
            render={({ field }) => (
              <AffiliationLogoField
                key={affiliation.id}
                value={field.value}
                onChange={field.onChange}
                disabled={updateAffiliationMutation.isPending}
              />
            )}
          />

          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
                <div>
                  <Label htmlFor="edit-affiliation-status">Status</Label>
                  <p className="text-sm text-gray-600">
                    {field.value ? 'Active' : 'Inactive'}
                  </p>
                </div>

                <Switch
                  id="edit-affiliation-status"
                  checked={field.value}
                  disabled={updateAffiliationMutation.isPending}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={updateAffiliationMutation.isPending}
              onClick={handleClose}
            >
              Cancel
            </Button>

            <div>
              <SubmitButton
                buttonTxt="Save Changes"
                submittingText="Saving..."
                status={updateAffiliationMutation.status}
              />
            </div>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
